import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import AppLayout from "../components/AppLayout";
import StreakCalendar from "../components/StreakCalendar";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Loader2, Flame, Target, TrendingUp, Clock, ExternalLink, CheckCircle2, Circle, Lock, ShieldCheck, ArrowRight, Image as ImageIcon, X, Upload, SkipForward } from "lucide-react";

function Stat({ icon: Icon, label, value, suffix, accent, sub }) {
  return (
    <div className="glass rounded-2xl p-5 hover:-translate-y-1 hover:border-white/20 transition-all">
      <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold">
        <Icon className={`w-4 h-4 ${accent || "text-primary"}`} /> {label}
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2">{value}<span className="text-base text-zinc-500 ml-1">{suffix}</span></div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </div>
  );
}

function StreakCard({ streak, best }) {
  const hot = streak >= 3;
  return (
    <div className={`glass rounded-2xl p-5 relative overflow-hidden hover:-translate-y-1 transition-all border ${hot ? "border-accent/40" : "border-white/10 hover:border-white/20"}`}>
      {hot && (
        <div className="absolute -top-6 -right-6 w-24 h-24 rounded-full bg-accent/20 blur-2xl pointer-events-none" />
      )}
      <div className="flex items-center gap-2 text-zinc-500 text-xs tracking-wider uppercase font-semibold">
        <Flame className={`w-4 h-4 ${hot ? "text-accent animate-pulse" : "text-accent"}`} /> Streak
      </div>
      <div className="font-heading text-3xl font-extrabold mt-2 flex items-baseline gap-1">
        {streak}
        <span className="text-base text-zinc-500 ml-1">days</span>
        {hot && <span className="text-xl ml-1">🔥</span>}
      </div>
      <div className="text-[11px] text-zinc-500 mt-1">Best: <span className="text-zinc-300 font-medium">{best || 0} days</span></div>
    </div>
  );
}

const MAX_IMAGE_MB = 2;

async function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function isImageProof(t) {
  const type = (t.proof_type || "").toLowerCase();
  return ["screenshot", "image", "photo", "picture"].some((k) => type.includes(k));
}

export default function Dashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [today, setToday] = useState(null);
  const [progress, setProgress] = useState({ readiness_score: 0, streak_count: 0, best_streak: 0, completion_rate: 0 });
  const [proofFor, setProofFor] = useState(null);
  const [proofText, setProofText] = useState("");
  const [proofImage, setProofImage] = useState(null); // {name, dataUrl}
  const [busy, setBusy] = useState(false);
  const imageInputRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [t, p] = await Promise.all([api.get("/tasks/today"), api.get("/progress")]);
      setToday(t.data);
      setProgress(p.data);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const onSwitch = () => { setLoading(true); load(); };
    window.addEventListener("adyap:goal-switched", onSwitch);
    return () => window.removeEventListener("adyap:goal-switched", onSwitch);
  }, [load]);

  const clearProof = () => {
    setProofFor(null); setProofText(""); setProofImage(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const handleImagePick = async (file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please pick an image file (PNG, JPG, WEBP).");
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      toast.error(`Image must be under ${MAX_IMAGE_MB}MB.`);
      return;
    }
    try {
      const dataUrl = await fileToBase64(file);
      setProofImage({ name: file.name, dataUrl });
    } catch {
      toast.error("Could not read the image. Try another file.");
    }
  };

  const completeTask = async (taskId, idx, proof) => {
    setBusy(true);
    try {
      const { data } = await api.put(`/tasks/${taskId}/complete`, { item_index: idx, proof_data: proof || null });
      if (data.progress) setProgress(data.progress);
      clearProof();
      toast.success("Task completed — progress updated!");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const skipTask = async (taskId, idx) => {
    if (!window.confirm("Skip this task? It will count as done (marked 'already known') and won't require proof.")) return;
    setBusy(true);
    try {
      const { data } = await api.put(`/tasks/${taskId}/skip`, { item_index: idx });
      if (data.progress) setProgress(data.progress);
      clearProof();
      toast.success("Marked as already known.");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const submitProof = (task, idx) => {
    const wantsImage = isImageProof(task);
    if (wantsImage) {
      // image is required (text is optional caption). If both, encode as JSON.
      if (!proofImage) { toast.error("Please attach an image as proof."); return; }
      const payload = proofText.trim()
        ? JSON.stringify({ image: proofImage.dataUrl, caption: proofText.trim(), filename: proofImage.name })
        : proofImage.dataUrl;
      completeTask(today.day.task_id, idx, payload);
    } else {
      if (!proofText.trim()) { toast.error("Please enter your proof."); return; }
      completeTask(today.day.task_id, idx, proofText.trim());
    }
  };

  if (loading) {
    return <AppLayout><div className="flex justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></AppLayout>;
  }

  if (!today || !today.day) {
    return (
      <AppLayout>
        <div className="text-center py-24">
          <Target className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="font-heading text-3xl tracking-tighter font-extrabold">No active roadmap yet</h1>
          <p className="text-zinc-400 mt-2">Set a goal and let AI build your day-by-day plan.</p>
          <button data-testid="start-onboarding-btn" onClick={() => navigate("/onboarding")}
            className="mt-6 inline-flex items-center gap-2 bg-primary hover:bg-primary/90 px-6 py-3 rounded-xl font-medium transition-colors">
            Build my roadmap <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </AppLayout>
    );
  }

  const day = today.day;
  const tasks = day.tasks || [];
  const doneCount = tasks.filter((t) => t.completed).length;

  return (
    <AppLayout>
      <div className="mb-8">
        <p className="text-xs tracking-wider uppercase font-semibold text-zinc-500">Training for</p>
        <h1 className="font-heading text-3xl sm:text-4xl tracking-tighter font-extrabold mt-1">{today.role_target}</h1>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8" data-testid="dashboard-stats">
        <Stat icon={TrendingUp} label="Readiness" value={progress.readiness_score} suffix="/100" />
        <StreakCard streak={progress.streak_count || 0} best={progress.best_streak || 0} />
        <Stat icon={CheckCircle2} label="Completion" value={progress.completion_rate} suffix="%" accent="text-emerald-400" />
        <Stat icon={Clock} label="Day" value={`${today.current_day}`} suffix={`/ ${today.total_days}`} />
      </div>

      <div className="mb-8">
        <StreakCalendar />
      </div>

      <div className="glass rounded-2xl p-6">
        <div className="flex items-center justify-between mb-1">
          <div>
            <h2 className="font-heading text-xl font-semibold">Today — Day {day.day_number}</h2>
            <p className="text-zinc-400 text-sm mt-0.5">{day.theme}</p>
          </div>
          <span className="text-sm text-zinc-400">{doneCount}/{tasks.length} done</span>
        </div>
        <div className="h-1.5 bg-white/10 rounded-full mt-4 overflow-hidden">
          <div className="h-full bg-primary transition-all" style={{ width: `${tasks.length ? (doneCount / tasks.length) * 100 : 0}%` }} />
        </div>

        <div className="mt-6 space-y-3" data-testid="today-tasks">
          {tasks.map((t, idx) => (
            <motion.div key={idx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: idx * 0.05 }}
              className={`rounded-xl border p-4 transition-all ${t.completed ? "border-emerald-500/30 bg-emerald-500/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"}`}>
              <div className="flex items-start gap-3">
                <button data-testid={`task-toggle-${idx}`} disabled={t.completed || busy}
                  onClick={() => (t.requires_proof && t.proof_type !== "none") ? setProofFor(idx) : completeTask(day.task_id, idx)}
                  className="mt-0.5 shrink-0">
                  {t.completed ? <CheckCircle2 className="w-5 h-5 text-emerald-400" /> : <Circle className="w-5 h-5 text-zinc-500 hover:text-primary" />}
                </button>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className={`font-medium ${t.completed ? "line-through text-zinc-500" : ""}`}>{t.title}</h3>
                    {t.requires_proof && t.proof_type !== "none" && !t.completed && (
                      <span className="text-[10px] tracking-wider uppercase font-semibold text-accent border border-accent/30 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <ShieldCheck className="w-3 h-3" /> proof
                      </span>
                    )}
                    {t.completed && t.skipped && (
                      <span className="text-[10px] tracking-wider uppercase font-semibold text-zinc-400 border border-white/15 rounded-full px-2 py-0.5 flex items-center gap-1">
                        <SkipForward className="w-3 h-3" /> skipped
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-zinc-400 mt-1">{t.description}</p>
                  <div className="flex items-center gap-3 flex-wrap mt-3">
                    <span className="text-xs text-zinc-500 flex items-center gap-1"><Clock className="w-3 h-3" /> {t.time_estimate_mins} min</span>
                    {(t.resources || []).map((r, ri) => {
                      // Support both old format (url, title) and new format (platform, search_term)
                      const href = r.url || (r.search_term ? `https://www.google.com/search?q=${encodeURIComponent(r.search_term)}+${encodeURIComponent(r.platform)}` : null);
                      const label = r.title || `${r.platform}${r.search_term ? `: ${r.search_term}` : ''}`;
                      return href ? (
                        <a key={ri} href={href} target="_blank" rel="noreferrer" data-testid={`resource-${idx}-${ri}`}
                          className="text-xs text-primary hover:underline flex items-center gap-1">
                          <ExternalLink className="w-3 h-3" /> {label}
                        </a>
                      ) : null;
                    })}
                    {!t.completed && (
                      <button
                        data-testid={`skip-task-${idx}`}
                        onClick={() => skipTask(day.task_id, idx)}
                        disabled={busy}
                        className="ml-auto text-xs text-zinc-400 hover:text-white flex items-center gap-1 border border-white/10 hover:border-white/25 px-2 py-1 rounded-md transition-colors disabled:opacity-50"
                        title="Mark as already known — counts as complete without proof"
                      >
                        <SkipForward className="w-3 h-3" /> Already know this
                      </button>
                    )}
                  </div>

                  {proofFor === idx && !t.completed && (
                    <div className="mt-3 rounded-xl border border-white/10 bg-white/[0.03] p-3" data-testid="proof-input-box">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-xs tracking-wider uppercase font-semibold text-zinc-400">
                          Submit proof <span className="text-zinc-500 normal-case tracking-normal">— {t.proof_type}</span>
                        </p>
                        <button onClick={clearProof} className="text-zinc-500 hover:text-white" aria-label="Cancel">
                          <X className="w-4 h-4" />
                        </button>
                      </div>

                      {isImageProof(t) ? (
                        <div className="space-y-3">
                          <input
                            ref={imageInputRef}
                            type="file"
                            accept="image/png,image/jpeg,image/jpg,image/webp,image/*"
                            className="hidden"
                            data-testid={`proof-image-input-${idx}`}
                            onChange={(e) => handleImagePick(e.target.files?.[0])}
                          />
                          {!proofImage ? (
                            <button
                              type="button"
                              onClick={() => imageInputRef.current?.click()}
                              className="w-full border-2 border-dashed border-white/15 hover:border-primary/50 rounded-xl py-8 flex flex-col items-center gap-2 transition-colors"
                              data-testid={`proof-image-pick-${idx}`}
                            >
                              <Upload className="w-6 h-6 text-zinc-400" />
                              <span className="text-sm text-zinc-400">Click to upload a screenshot or photo</span>
                              <span className="text-[11px] text-zinc-500">PNG, JPG or WEBP · up to {MAX_IMAGE_MB}MB</span>
                            </button>
                          ) : (
                            <div className="relative rounded-xl overflow-hidden border border-white/10 bg-black/40">
                              <img src={proofImage.dataUrl} alt="Proof preview" className="w-full max-h-64 object-contain" />
                              <button
                                type="button"
                                onClick={() => { setProofImage(null); if (imageInputRef.current) imageInputRef.current.value = ""; }}
                                className="absolute top-2 right-2 bg-black/70 hover:bg-black text-white rounded-full p-1.5"
                                aria-label="Remove image"
                              >
                                <X className="w-3.5 h-3.5" />
                              </button>
                              <div className="px-3 py-2 text-[11px] text-zinc-400 flex items-center gap-1.5 border-t border-white/10">
                                <ImageIcon className="w-3.5 h-3.5" /> {proofImage.name}
                              </div>
                            </div>
                          )}
                          <input
                            value={proofText}
                            onChange={(e) => setProofText(e.target.value)}
                            placeholder="Optional caption / note"
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                          />
                          <div className="flex justify-end">
                            <button
                              data-testid={`submit-proof-${idx}`}
                              disabled={!proofImage || busy}
                              onClick={() => submitProof(t, idx)}
                              className="bg-primary hover:bg-primary/90 text-black px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                            >
                              {busy ? "Submitting…" : "Submit proof"}
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <input
                            value={proofText}
                            onChange={(e) => setProofText(e.target.value)}
                            placeholder={`Paste your ${t.proof_type} (quiz score, repo link, etc.)`}
                            className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-primary"
                          />
                          <button
                            data-testid={`submit-proof-${idx}`}
                            disabled={!proofText.trim() || busy}
                            onClick={() => submitProof(t, idx)}
                            className="bg-primary hover:bg-primary/90 text-black px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                          >
                            Submit proof
                          </button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <div className="mt-4 text-xs text-zinc-500 flex items-center gap-2">
        <Lock className="w-3 h-3" /> Finish each day&apos;s tasks to unlock the next — go as far as you can today.
      </div>
    </AppLayout>
  );
}
